import { useState } from "react";
import { Button, ErrorMessage, Flex, Text, Textarea } from "@jsq/ds/core";
import { withClientContext } from "~/apollo/client";
import { useAddDdqProdOpsWorkflowCommentMutation } from "~/graphql/supergraphHooks";
import { SPACING } from "~/styles/variables";
import { ActivityFeed } from "../../shared/components/ActivityFeed";
import * as styles from "./styles_vanilla.css";
import { useDdqProdOpsWorkflowLog } from "./useDdqProdOpsWorkflowLog";

export type LogTabProps = {
  workflowId: string;
};

export const LogTab = ({ workflowId }: LogTabProps) => {
  const [comment, setComment] = useState("");
  const [postError, setPostError] = useState<string | null>(null);
  const { entries, hasMore, isLoadingMore, onLoadMore } = useDdqProdOpsWorkflowLog(workflowId);

  const [addComment, { loading: isPosting }] = useAddDdqProdOpsWorkflowCommentMutation({
    context: withClientContext("federation"),
    refetchQueries: ["GetDdqProdOpsWorkflowLog"],
  });

  const onPost = () => {
    setPostError(null);
    addComment({ variables: { workflowId, body: comment.trim() } })
      .then(() => setComment(""))
      .catch((error: unknown) => {
        setPostError(error instanceof Error ? error.message : "Something went wrong.");
      });
  };

  return (
    <Flex
      flexDirection="column"
      gap={SPACING.MEDIUM}
      className={styles.card}
      data-testid="ddq-drawer-log-tab-panel"
    >
      <Text variant="h3">Log</Text>

      <Flex flexDirection="column" gap={SPACING.SMALL}>
        <Textarea
          isFullWidth
          label="Add a comment"
          placeholder="Leave a note on this request"
          value={comment}
          onSetValue={setComment}
          data-testid="ddq-drawer-log-comment"
        />
        {postError && (
          <ErrorMessage data-testid="ddq-drawer-log-comment-error">{postError}</ErrorMessage>
        )}
        <Flex justifyContent="flex-end">
          <Button
            isLoading={isPosting}
            disabled={!comment.trim() || isPosting}
            onClick={onPost}
            data-testid="ddq-drawer-log-post"
          >
            Post
          </Button>
        </Flex>
      </Flex>

      <ActivityFeed entries={entries} emptyLabel="No activity logged yet" />

      {hasMore && (
        <Flex justifyContent="center">
          <Button
            variant="neutral"
            isLoading={isLoadingMore}
            onClick={onLoadMore}
            data-testid="ddq-drawer-log-load-more"
          >
            Load more
          </Button>
        </Flex>
      )}
    </Flex>
  );
};
